import { useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type ArchivedDocument,
  type Company,
  type CostCategory,
  type CostVatRow,
  type Counterparty,
  type NewCost,
  type VatDeduction,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as money } from '../format';
import { CounterpartyDialog } from './CounterpartyDialog';
import * as Icon from './Icons';

/**
 * Kreator księgowania (E17-S3): od pliku w skrzynce do dokumentu kosztowego, wpłaty i dekretu —
 * pytaniami, bez księgowego słownictwa.
 *
 * Ścieżki: faktura kosztowa, paragon (koszt bez odliczenia VAT), wyciąg bankowy (import
 * i dopasowanie wpłat). Każdy krok da się przerwać: odpowiedzi zapisują się przy dokumencie,
 * a dokument zostaje „w toku”. Ostatni krok pokazuje, co powstanie, zanim cokolwiek powstanie.
 * Dokument nie księguje się sam: przy pełnych księgach dekret trafia na listę „do zaksięgowania”.
 */

export type WizardKind = 'COST_INVOICE' | 'RECEIPT' | 'BANK_STATEMENT';

export type WizardState = {
  kind: WizardKind | null;
  step: number;
  counterpartyId: string | null;
  cost: NewCost & { dueDate: string | null };
  payment: { paid: boolean; paidOn: string; method: string };
};

const CATEGORIES: CostCategory[] = ['GOODS', 'PURCHASE_COSTS', 'SALARIES', 'OTHER'];
const RATES: CostVatRow['vatRate'][] = ['23', '8', '5', '0', 'zw', 'np'];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function emptyWizard(): WizardState {
  return {
    kind: null,
    step: 0,
    counterpartyId: null,
    cost: {
      documentNumber: '',
      counterpartyId: null,
      issueDate: today(),
      costDate: today(),
      dueDate: null,
      category: 'OTHER',
      vatDeduction: 'FULL',
      fixedAsset: false,
    // Kreator nie pyta o WNT: dokument z Unii oznacza się przy edycji kosztu
    intraCommunity: false,
      description: '',
      vatRows: [{ vatRate: '23', net: '0.00', vat: '0.00' }],
    },
    payment: { paid: false, paidOn: today(), method: 'przelew' },
  };
}

/** Ile wejdzie w koszty i ile VAT-u da się odliczyć — do pokazania, zanim cokolwiek powstanie. */
export function summarise(rows: CostVatRow[], deduction: VatDeduction) {
  const net = rows.reduce((sum, row) => sum + Number(String(row.net).replace(',', '.') || 0), 0);
  const vat = rows.reduce((sum, row) => sum + Number(String(row.vat).replace(',', '.') || 0), 0);
  const deductible = deduction === 'FULL' ? vat : deduction === 'HALF' ? vat / 2 : 0;
  return { net, vat, gross: net + vat, deductible, costAmount: net + (vat - deductible) };
}

/** Kroki ścieżki: paragon nie ma kontrahenta ani odliczenia, wyciąg ma tylko import. */
export function stepsOf(kind: WizardKind | null): ('kind' | 'counterparty' | 'document' | 'payment' | 'summary' | 'statement')[] {
  switch (kind) {
    case 'COST_INVOICE':
      return ['kind', 'counterparty', 'document', 'payment', 'summary'];
    case 'RECEIPT':
      return ['kind', 'document', 'payment', 'summary'];
    case 'BANK_STATEMENT':
      return ['kind', 'statement'];
    default:
      return ['kind'];
  }
}

export function PostingWizard({
  company,
  document,
  t,
  locale,
  onClose,
  onFinished,
}: {
  company: Company;
  document: ArchivedDocument;
  t: Dictionary;
  locale: Locale;
  onClose: () => void;
  /** Po zakończeniu: gdzie iść dalej (wyciąg → rozrachunki, koszt → dziennik przy pełnych księgach). */
  onFinished: (next: 'settlements' | 'journal' | 'costs') => void;
}) {
  const [state, setState] = useState<WizardState | null>(null);
  const [suppliers, setSuppliers] = useState<Counterparty[]>([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.documentWizard(company.id, document.id), api.counterparties(company.id, '', false)])
      .then(([saved, loaded]) => {
        const base = emptyWizard();
        const restored = saved.state ? ({ ...base, ...(saved.state as Partial<WizardState>) } as WizardState) : base;
        // Numer z nazwy pliku to dobry start, gdy nikt jeszcze nic nie wpisał
        if (!restored.cost.documentNumber && document.fileName) {
          restored.cost.documentNumber = document.fileName.replace(/\.[a-z0-9]+$/i, '');
        }
        setState(restored);
        setSuppliers(loaded);
      })
      .catch((failure) => setError(failure instanceof ApiError ? failure.message : String(failure)));
  }, [company.id, document.id, document.fileName]);

  const steps = useMemo(() => stepsOf(state?.kind ?? null), [state?.kind]);
  const current = steps[state?.step ?? 0] ?? 'kind';
  const sum = useMemo(() => (state ? summarise(state.cost.vatRows, state.cost.vatDeduction) : null), [state]);
  const fullBooks = company.booksType === 'FULL';

  async function persist(next: WizardState) {
    setState(next);
    try {
      await api.saveDocumentWizard(company.id, document.id, next);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }

  function go(delta: number) {
    if (!state) return;
    const target = Math.min(Math.max(state.step + delta, 0), steps.length - 1);
    void persist({ ...state, step: target });
  }

  function update(patch: Partial<WizardState>) {
    if (!state) return;
    setState({ ...state, ...patch });
  }

  const updateRow = (index: number, change: Partial<CostVatRow>) => {
    if (!state) return;
    update({ cost: { ...state.cost, vatRows: state.cost.vatRows.map((row, at) => (at === index ? { ...row, ...change } : row)) } });
  };

  /** Koniec: dokument kosztowy → zaksięgowanie → wpłata (jeśli była) → powiązanie pliku. */
  async function finish() {
    if (!state || !sum) return;
    setBusy(true);
    setError(null);
    try {
      const receipt = state.kind === 'RECEIPT';
      const created = await api.addCost(company.id, {
        ...state.cost,
        counterpartyId: receipt ? null : state.counterpartyId,
        vatDeduction: receipt ? 'NONE' : state.cost.vatDeduction,
        description: state.cost.description || null,
        vatRows: state.cost.vatRows.map((row) => ({ ...row, net: String(row.net).replace(',', '.'), vat: String(row.vat).replace(',', '.') })),
      });
      await api.bookCost(company.id, created.id);
      if (state.payment.paid) {
        await api.payCost(company.id, created.id, { paidOn: state.payment.paidOn, amount: sum.gross.toFixed(2), method: state.payment.method || null });
      }
      await api.linkDocumentToCost(company.id, document.id, created.id);
      setDone(fullBooks ? t.wizard.doneFull : t.wizard.doneSimple);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  /** Wyciąg: plik z archiwum trafia do importu, a dalej działa dopasowanie wpłat (E10). */
  async function importStatement() {
    setBusy(true);
    setError(null);
    try {
      const file = await api.documentContent(company.id, document.id);
      const content = await file.blob.text();
      await api.importBankStatement(company.id, content);
      await api.acceptDocument(company.id, document.id);
      setDone(t.wizard.doneStatement);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!state) {
    return (
      <div className="panel">
        <p className="muted">{t.loading}</p>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (done) {
    return (
      <div className="panel">
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{done}</b>
          </div>
        </div>
        <div className="row">
          <button type="button" className="btn" onClick={() => onFinished(state.kind === 'BANK_STATEMENT' ? 'settlements' : fullBooks ? 'journal' : 'costs')}>
            {state.kind === 'BANK_STATEMENT' ? t.wizard.goMatch : fullBooks ? t.wizard.goJournal : t.wizard.goCosts}
          </button>
          <button type="button" className="btn plain" onClick={onClose}>
            {t.wizard.close}
          </button>
        </div>
      </div>
    );
  }

  const supplier = suppliers.find((item) => item.id === state.counterpartyId) ?? null;

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>
            {t.wizard.title} · {document.fileName}
          </h4>
          <p>
            {t.wizard.stepOf(state.step + 1, steps.length)}: {t.wizard.steps[current]}
          </p>
        </div>
        <div className="sp">
          <button type="button" className="btn ghost sm" onClick={onClose}>
            {t.wizard.later}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {current === 'kind' && (
        <div className="row" role="radiogroup" aria-label={t.wizard.steps.kind}>
          {(['COST_INVOICE', 'RECEIPT', 'BANK_STATEMENT'] as WizardKind[]).map((kind) => (
            <label key={kind} className="row" style={{ alignItems: 'center', gap: 8 }}>
              <input type="radio" name="kind" checked={state.kind === kind} onChange={() => update({ kind, step: 0 })} />
              <span>
                <b>{t.wizard.kinds[kind]}</b>
                <div className="hint">{t.wizard.kindHints[kind]}</div>
              </span>
            </label>
          ))}
        </div>
      )}

      {current === 'counterparty' && (
        <>
          <p className="hint">{t.wizard.counterpartyHint}</p>
          <div className="row">
            <label className="fld" style={{ flex: 2, minWidth: 260 }}>
              <span>{t.wizard.supplier}</span>
              <select className="inp" value={state.counterpartyId ?? ''} onChange={(event) => update({ counterpartyId: event.target.value || null })}>
                <option value="">—</option>
                {suppliers.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.nip ? ` · ${item.nip}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="fld">
              <span>&nbsp;</span>
              <button type="button" className="btn sec" onClick={() => setAdding(true)}>
                <Icon.Plus />
                {t.wizard.newSupplier}
              </button>
            </div>
          </div>
          {adding && (
            <CounterpartyDialog
              companyId={company.id}
              t={t}
              onCreated={(created) => {
                setSuppliers([created, ...suppliers]);
                update({ counterpartyId: created.id });
                setAdding(false);
              }}
              onClose={() => setAdding(false)}
            />
          )}
        </>
      )}

      {current === 'document' && (
        <>
          <div className="row">
            <label className="fld" style={{ flex: 1, minWidth: 200 }}>
              <span>{t.wizard.documentNumber}</span>
              <input className="inp" value={state.cost.documentNumber} onChange={(event) => update({ cost: { ...state.cost, documentNumber: event.target.value } })} />
            </label>
            <label className="fld">
              <span>{t.wizard.issueDate}</span>
              <input className="inp" type="date" value={state.cost.issueDate} onChange={(event) => update({ cost: { ...state.cost, issueDate: event.target.value, costDate: event.target.value } })} />
            </label>
            <label className="fld">
              <span>{t.wizard.dueDate}</span>
              <input className="inp" type="date" value={state.cost.dueDate ?? ''} onChange={(event) => update({ cost: { ...state.cost, dueDate: event.target.value || null } })} />
            </label>
            <label className="fld">
              <span>{t.wizard.category}</span>
              <select className="inp" value={state.cost.category} onChange={(event) => update({ cost: { ...state.cost, category: event.target.value as CostCategory } })}>
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t.costs.categories[category]}
                  </option>
                ))}
              </select>
            </label>
            {state.kind === 'COST_INVOICE' && (
              <label className="fld">
                <span>{t.wizard.deduction}</span>
                <select className="inp" value={state.cost.vatDeduction} onChange={(event) => update({ cost: { ...state.cost, vatDeduction: event.target.value as VatDeduction } })}>
                  {(['FULL', 'HALF', 'NONE'] as VatDeduction[]).map((value) => (
                    <option key={value} value={value}>
                      {t.wizard.deductions[value]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.wizard.vatRate}</th>
                  <th className="n">{t.wizard.net}</th>
                  <th className="n">{t.wizard.vat}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.cost.vatRows.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <select className="inp sm" aria-label={`${t.wizard.vatRate} ${index + 1}`} value={row.vatRate} onChange={(event) => updateRow(index, { vatRate: event.target.value as CostVatRow['vatRate'] })}>
                        {RATES.map((rate) => (
                          <option key={rate} value={rate}>
                            {rate}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="n">
                      <input className="inp mono sm" style={{ maxWidth: 120, textAlign: 'right' }} inputMode="decimal" aria-label={`${t.wizard.net} ${index + 1}`} value={row.net} onChange={(event) => updateRow(index, { net: event.target.value })} />
                    </td>
                    <td className="n">
                      <input className="inp mono sm" style={{ maxWidth: 120, textAlign: 'right' }} inputMode="decimal" aria-label={`${t.wizard.vat} ${index + 1}`} value={row.vat} onChange={(event) => updateRow(index, { vat: event.target.value })} />
                    </td>
                    <td className="act">
                      {state.cost.vatRows.length > 1 && (
                        <button type="button" className="btn ghost sm" onClick={() => update({ cost: { ...state.cost, vatRows: state.cost.vatRows.filter((_, at) => at !== index) } })}>
                          {t.journal.removeLine}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row">
            <button type="button" className="btn ghost sm" onClick={() => update({ cost: { ...state.cost, vatRows: [...state.cost.vatRows, { vatRate: '23', net: '0.00', vat: '0.00' }] } })}>
              {t.journal.addLine}
            </button>
            {sum && (
              <span className="chip">
                {t.wizard.gross}: {money(sum.gross, locale)}
              </span>
            )}
          </div>
        </>
      )}

      {current === 'payment' && (
        <div className="row">
          <label className="row" style={{ alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={state.payment.paid} onChange={(event) => update({ payment: { ...state.payment, paid: event.target.checked } })} />
            <span>{t.wizard.paid}</span>
          </label>
          {state.payment.paid && (
            <>
              <label className="fld">
                <span>{t.wizard.paidOn}</span>
                <input className="inp" type="date" value={state.payment.paidOn} onChange={(event) => update({ payment: { ...state.payment, paidOn: event.target.value } })} />
              </label>
              <label className="fld">
                <span>{t.wizard.method}</span>
                <select className="inp" value={state.payment.method} onChange={(event) => update({ payment: { ...state.payment, method: event.target.value } })}>
                  <option value="przelew">{t.wizard.methods.transfer}</option>
                  <option value="gotówka">{t.wizard.methods.cash}</option>
                  <option value="karta">{t.wizard.methods.card}</option>
                </select>
              </label>
            </>
          )}
          {!state.payment.paid && <p className="hint">{t.wizard.unpaidHint}</p>}
        </div>
      )}

      {current === 'summary' && sum && (
        <>
          <p>{t.wizard.summaryLead}</p>
          <div className="tbl">
            <table>
              <tbody>
                <tr>
                  <td>{t.wizard.kinds[state.kind ?? 'COST_INVOICE']}</td>
                  <td className="wrap">
                    <b>{state.cost.documentNumber || '—'}</b> · {state.cost.issueDate}
                    {supplier ? ` · ${supplier.name}` : ''}
                  </td>
                </tr>
                <tr>
                  <td>{t.wizard.gross}</td>
                  <td className="mono">{money(sum.gross, locale)}</td>
                </tr>
                <tr>
                  <td>{t.wizard.costAmount}</td>
                  <td className="mono">{money(state.kind === 'RECEIPT' ? sum.gross : sum.costAmount, locale)}</td>
                </tr>
                <tr>
                  <td>{t.wizard.deductibleVat}</td>
                  <td className="mono">{money(state.kind === 'RECEIPT' ? 0 : sum.deductible, locale)}</td>
                </tr>
                <tr>
                  <td>{t.wizard.steps.payment}</td>
                  <td>{state.payment.paid ? `${t.wizard.paidShort} ${state.payment.paidOn}` : t.wizard.unpaidShort}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="hint">{fullBooks ? t.wizard.summaryFull : t.wizard.summarySimple}</p>
        </>
      )}

      {current === 'statement' && (
        <>
          <p>{t.wizard.statementLead}</p>
          <div className="row">
            <button type="button" className="btn" disabled={busy} onClick={() => void importStatement()}>
              {t.wizard.importStatement}
            </button>
          </div>
        </>
      )}

      {current !== 'statement' && (
        <div className="row">
          {state.step > 0 && (
            <button type="button" className="btn plain" disabled={busy} onClick={() => go(-1)}>
              {t.wizard.back}
            </button>
          )}
          {current !== 'summary' ? (
            <button
              type="button"
              className="btn"
              disabled={busy || (current === 'kind' && !state.kind) || (current === 'counterparty' && !state.counterpartyId) || (current === 'document' && (!state.cost.documentNumber.trim() || !sum || sum.gross <= 0))}
              onClick={() => go(1)}
            >
              {t.wizard.next}
            </button>
          ) : (
            <button type="button" className="btn" disabled={busy} onClick={() => void finish()}>
              {t.wizard.finish}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
